import { auth, db } from './firebase';
import { doc, getDoc, serverTimestamp, updateDoc, setDoc } from 'firebase/firestore';

export type FlashcardLimits = {
  perGeneration: number;
  dailyLimit: number;
};

// Regras: free = 5 por dia (5 por geração). NOMEADO/Premium = 30 por geração (prático ilimitado no dia).
const FREE_LIMITS: FlashcardLimits = { perGeneration: 5, dailyLimit: 5 };
const PRO_LIMITS: FlashcardLimits = { perGeneration: 30, dailyLimit: 999 };

const normalizePlan = (plan?: string | null) => {
  if (!plan) return 'free';
  const p = plan.toLowerCase();
  if (p.includes('influencer')) return 'influencer';
  if (p.includes('premium')) return 'premium';
  if (p.includes('essencial') || p.includes('nomeado') || p === 'essential' || p === 'pro') return 'essencial';
  return 'free';
};

export const getFlashcardLimits = (plan?: string | null): FlashcardLimits => {
  const normalized = normalizePlan(plan);
  return normalized === 'essencial' || normalized === 'premium' || normalized === 'influencer' ? PRO_LIMITS : FREE_LIMITS;
};

export const canUseFlashcards = async (): Promise<{ canContinue: boolean; remainingToday: number; perGeneration: number; dailyLimit: number; isPro: boolean; isInfluencer: boolean }> => {
  const user = auth.currentUser;
  if (!user) return { canContinue: false, remainingToday: 0, perGeneration: FREE_LIMITS.perGeneration, dailyLimit: FREE_LIMITS.dailyLimit, isPro: false, isInfluencer: false };

  const userRef = doc(db, 'users', user.uid);
  let snap = await getDoc(userRef);

  // Se o documento não existe, cria com entitlements padrão (free) para evitar bloqueio
  if (!snap.exists()) {
    await setDoc(userRef, {
      entitlements: {
        plan: 'free',
        flashcardsUsed: 0,
        lastFlashcardResetDate: new Date().toISOString().split('T')[0],
      },
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    }, { merge: true });
    snap = await getDoc(userRef);
  }

  const ent = snap.data()?.entitlements || {};
  const influencer = Boolean(ent.influencer);
  const plan = influencer ? 'influencer' : ent.isPro ? 'premium' : normalizePlan(ent.plan);
  const limits = getFlashcardLimits(plan);

  const today = new Date().toISOString().split('T')[0];
  const used = ent.flashcardsUsed || 0;
  const lastDate = ent.lastFlashcardResetDate || today;

  const isToday = lastDate === today;
  const remainingToday = isToday ? Math.max(0, limits.dailyLimit - used) : limits.dailyLimit;
  const canContinue = remainingToday > 0;
  const isPro = plan !== 'free';
  const isInfluencer = influencer;

  // Se não é hoje, reseta contagem
  if (!isToday) {
    await updateDoc(userRef, {
      'entitlements.flashcardsUsed': 0,
      'entitlements.lastFlashcardResetDate': today,
      updatedAt: serverTimestamp(),
    });
  }

  return { canContinue, remainingToday, perGeneration: limits.perGeneration, dailyLimit: limits.dailyLimit, isPro, isInfluencer };
};

export const incrementFlashcardsUsed = async (count: number) => {
  const user = auth.currentUser;
  if (!user) return;
  const userRef = doc(db, 'users', user.uid);
  const snap = await getDoc(userRef);
  const today = new Date().toISOString().split('T')[0];

  const ent = snap.exists() ? snap.data().entitlements || {} : {};
  const lastDate = ent.lastFlashcardResetDate || today;
  const isToday = lastDate === today;
  const used = isToday ? (ent.flashcardsUsed || 0) : 0;
  const newUsed = used + count;

  try {
    await updateDoc(userRef, {
      'entitlements.flashcardsUsed': newUsed,
      'entitlements.lastFlashcardResetDate': today,
      updatedAt: serverTimestamp(),
    });
  } catch (err) {
    // Se o documento não existia, cria com os valores corretos
    await setDoc(userRef, {
      entitlements: {
        plan: ent.plan || 'free',
        flashcardsUsed: newUsed,
        lastFlashcardResetDate: today,
      },
      updatedAt: serverTimestamp(),
    }, { merge: true });
  }
};
